import { useState, useEffect, useRef, useCallback } from "react";
import { CardA } from "../../ui/Cards";
import {
  useConnectorCalibrate,
  useServerCalibrate,
  useScanProgress,
  useSaveCalibrationScan,
  cancelScan,
  useConnectorAppHealth,
} from "../../../api";
import { useSampleContext } from "../../../contexts";
import ScanningProgress from "../../ui/ScanningProgress";
import CancelScanModal from "../../ui/CancelScanModal";

const WizardCalibrateScan = ({ step, changeStep, pageID }) => {
  const { device } = useSampleContext();

  // Check if connector is legacy (based on health endpoint support)
  const connectorAppHealth = useConnectorAppHealth();
  const isLegacyConnector = connectorAppHealth.data?.healthSupported === false;
  const [scanProgress, setScanProgress] = useState({});
  const [scanSteps, setScanSteps] = useState(null);
  const [scanId, setScanId] = useState(null);
  const [scanResultData, setScanResultData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [readyToSave, setReadyToSave] = useState(false); // Triggers save when SSE completes

  const awaitingScanRef = useRef(false);
  const awaitingSaveRef = useRef(false);
  const saveCompletedRef = useRef(false); // Prevent duplicate saves
  const userCancelledRef = useRef(false); // Track user-initiated cancellation
  const scanCompleteRef = useRef(false); // Track scan success/failure for onServerSuccess
  const errorRef = useRef(false); // Track error state for onServerSuccess

  const connectorCalibration = useConnectorCalibrate(device.deviceId);
  const saveCalibrationScan = useSaveCalibrationScan(
    device.deviceId,
    scanResultData,
    undefined,
    (err) => errorLogger("save", err),
  );
  const serverCalibration = useServerCalibrate(
    device.deviceId,
    Date.now(),
    () => scanCompleteRef.current,
    undefined, // resolved manually below, after saveCalibrationScan also settles
    (err) => errorLogger("server", err),
  );

  // Clear previous calibration error when mounting this step
  useEffect(() => {
    if (step === pageID) {
      setInlineError("");
      setScanId(null);
      setScanSteps(null);
      setScanResultData(null);
      sessionStorage.removeItem("calibrationErrorMessage");
      sessionStorage.removeItem("calibrationErrorStage");
    }
  }, [step, pageID]);

  function errorLogger(stage, err) {
    setLoading(false); // deactivate loading spinner

    // Detect connector app connection errors
    const isConnectorConnectionError =
      err?.code === "ECONNREFUSED" ||
      err?.code === "ENOTFOUND" ||
      err?.code === "ECONNABORTED" ||
      err?.code === "ETIMEDOUT" ||
      err?.message?.includes("Network Error") ||
      err?.message?.includes("timeout") ||
      err?.message?.includes("ECONNREFUSED") ||
      err?.message?.includes("ERR_NETWORK") ||
      err?.message?.includes("ERR_CONNECTION_REFUSED");

    // Extract readable message
    let message;
    if (isConnectorConnectionError && stage === "connector") {
      message =
        "Connector App unavailable. Please ensure the Connector App is running and try again.";
    } else {
      message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        (stage === "connector"
          ? "Connector calibration request failed. Ensure the Connector App is running and device is connected."
          : "Server calibration save failed. Please retry.");
    }
    console.error("Calibration error (" + stage + "):", err);

    // Store for fail screen consumption
    sessionStorage.setItem("calibrationErrorMessage", message);
    sessionStorage.setItem("calibrationErrorStage", stage);

    // Show small inline helper if user remains here (fast failure before navigation)
    setInlineError(message);
    changeStep("fail");
  }

  // Memoize SSE callbacks to prevent EventSource recreation on every render
  const handleCalibrationProgress = useCallback(
    (progress) => setScanProgress(progress),
    [],
  );

  const handleCalibrationComplete = useCallback((result) => {
    // Scan complete - extract actual scan data from SSE result (just like ScanWizard)
    if (awaitingScanRef.current && !userCancelledRef.current) {
      // Extract the scan result data (wavelengths, intensities, etc.)
      const scanData = result;

      if (scanData.success === false) {
        // Scan failed - but still save for troubleshooting
        if (!scanData.data.deviceSerial) {
          scanData.data.deviceSerial = device.deviceId; // Ensure device ID is included
        }
        setScanResultData(scanData);
        scanCompleteRef.current = false; // Update ref for onServerSuccess
        errorRef.current = true; // Update ref for onServerSuccess
        awaitingSaveRef.current = true; // Save failed scan for troubleshooting
        awaitingScanRef.current = false;
        setReadyToSave(true); // Trigger save effect
        // Store error message but don't call errorLogger yet (let save complete first)
        sessionStorage.setItem(
          "calibrationErrorMessage",
          scanData?.error?.message ||
            "Scan failed. Please try again or contact support.",
        );
        sessionStorage.setItem("calibrationErrorStage", "connector");
      } else {
        // Scan succeeded - prepare to save
        setScanResultData(scanData);
        scanCompleteRef.current = true; // Update ref for onServerSuccess
        errorRef.current = false; // Update ref for onServerSuccess
        awaitingSaveRef.current = true;
        awaitingScanRef.current = false;
        setReadyToSave(true); // Trigger save effect
      }
    }
  }, []);

  const handleCalibrationError = useCallback((error) => {
    // SSE error or timeout - but ignore if user cancelled
    if (userCancelledRef.current) {
      // User cancelled, don't show error
      awaitingScanRef.current = false;
      return;
    }

    setScanId(null);
    setLoading(false); // deactivate loading spinner

    // Extract appropriate error message based on error type
    let errorMessage;
    if (error?.timeout) {
      errorMessage = "Calibration scan timed out. Please try again.";
    } else if (error?.message) {
      errorMessage = error.message;
    } else {
      errorMessage = "Unexpected error during calibration scan.";
    }

    errorLogger("connector", { message: errorMessage });
    awaitingScanRef.current = false;
  }, []);

  // Monitor scan progress via SSE - measurement data comes from here
  useScanProgress(
    scanId,
    handleCalibrationProgress,
    handleCalibrationComplete,
    handleCalibrationError,
  );

  // Extract scan_id and steps from connector response
  // For modern connectors: extract scan_id for SSE tracking
  // For legacy connectors: extract calibration data directly from response
  useEffect(() => {
    if (!awaitingScanRef.current) return;
    if (!connectorCalibration) return;
    const {
      data,
      isFetching,
      status,
      error: queryError,
    } = connectorCalibration;

    // Handle query errors (network failures, timeouts, etc.)
    if (loading && !isFetching && status === "error" && step === pageID) {
      awaitingScanRef.current = false;
      errorLogger(
        "connector",
        queryError || { message: "Failed to connect to Connector App" },
      );
      return;
    }

    if (loading && !isFetching && status === "success" && step === pageID) {
      // For legacy connectors, allow failed calibrations through for saving
      // Only block on connection/protocol errors (not calibration failures)
      if (data?.status >= 400 && !isLegacyConnector) {
        // Error response from modern connector (caught and returned as success)
        awaitingScanRef.current = false;
        errorLogger("connector", {
          message: data.message || "Failed to connect to Connector App",
          code: data.code,
        });
        return;
      }

      // Legacy connector: calibration data returned directly
      if (isLegacyConnector) {
        const calibrationData = data?.calibrationData || data?.data || data;
        calibrationData.detail = calibrationData?.detail?.replace(
          "sucessful",
          "successful",
        ); // Fix legacy typo

        // Check if calibration was successful
        // Legacy connector returns a detail field with success/error message
        // Success contains "sucessful" (with typo) or "successful"
        const detailLower = calibrationData?.detail?.toLowerCase() || "";
        const isFailure = !detailLower.includes("successful");

        if (isFailure) {
          // Calibration failed - but still save for troubleshooting
          // Ensure we have a valid object structure even for failed scans
          const failedScanData = {
            success: false,
            message:
              calibrationData?.message ||
              calibrationData?.detail ||
              "Calibration scan failed",
            errorFlag: calibrationData?.errorFlag ?? -1,
            // Include any spectral data if available
            ...calibrationData,
          };
          setScanResultData(failedScanData);
          scanCompleteRef.current = false; // Update ref for onServerSuccess
          errorRef.current = true; // Update ref for onServerSuccess
          // Keep loading=true until save completes (onServerSuccess will set it to false)
          awaitingSaveRef.current = true; // Save for troubleshooting
          awaitingScanRef.current = false;
          setReadyToSave(true); // Trigger save effect
          sessionStorage.setItem(
            "calibrationErrorMessage",
            calibrationData?.message ||
              calibrationData?.detail ||
              "Calibration failed without a specific error message.",
          );
          sessionStorage.setItem("calibrationErrorStage", "connector");
        } else {
          // Calibration succeeded
          setScanResultData(calibrationData);
          scanCompleteRef.current = true; // Update ref for onServerSuccess
          errorRef.current = false; // Update ref for onServerSuccess
          awaitingSaveRef.current = true;
          awaitingScanRef.current = false;
          // Keep loading=true until save completes (onServerSuccess will set it to false)
          setReadyToSave(true); // Trigger save effect
        }
      } else {
        // Modern connector: extract scan_id for SSE tracking
        const id = data?.scan_id || data?.data?.scan_id;
        if (id) setScanId(id);
        const steps = data?.scanSteps || data?.data?.scanSteps;
        if (steps) setScanSteps(steps);
      }
    }
  }, [connectorCalibration, loading, step, pageID, isLegacyConnector]);

  // Save calibration data after SSE completes and scanResultData is available
  // readyToSave state ensures this effect fires even if scanResultData doesn't change
  useEffect(() => {
    if (
      readyToSave &&
      scanResultData &&
      awaitingSaveRef.current &&
      !saveCompletedRef.current &&
      !userCancelledRef.current
    ) {
      saveCompletedRef.current = true; // Mark as attempting to save
      setReadyToSave(false); // Reset trigger for next scan

      // Save the calibration audit record AND update the server's lastCalibration
      // timestamp together, and wait for both to settle before deciding the
      // outcome. Advancing on the PATCH alone let a failed/slow audit-record
      // save go unnoticed - the wizard would unmount (on PATCH success) before
      // the save's own result was known, orphaning it silently.
      Promise.all([saveCalibrationScan.refetch(), serverCalibration.refetch()])
        .then(([saveResult, serverResult]) => {
          if (userCancelledRef.current) return;

          // Only treat a failed audit-record save as fatal when the scan itself
          // succeeded - if the scan already failed, this save was best-effort
          // (for troubleshooting) and shouldn't override the original scan error.
          if (
            saveResult?.isError &&
            scanCompleteRef.current &&
            !errorRef.current
          ) {
            errorLogger(
              "save",
              saveResult.error || {
                message: "Failed to save calibration scan.",
              },
            );
            return;
          }

          onServerSuccess(serverResult?.data);
        })
        .catch((err) => {
          if (userCancelledRef.current) return;
          errorLogger("save", err);
        });
    }
  }, [
    readyToSave,
    scanResultData,
    saveCalibrationScan.refetch,
    serverCalibration.refetch,
  ]);

  // Callback used when the server query finishes to check the results
  function onServerSuccess(data) {
    // If user cancelled, don't process the result
    if (userCancelledRef.current) {
      return;
    }

    if (data) {
      setLoading(false); // deactivate loading spinner
      if (data.status && data.status >= 200 && data.status < 300) {
        // Save was successful (200s)
        // Use refs to get current values (not stale closure values)
        if (errorRef.current || !scanCompleteRef.current) {
          // Scan failed but data was saved for troubleshooting
          // Use the pre-stored calibration error message, not the server response
          changeStep("fail");
        } else {
          // Scan succeeded and data saved - update the last calibration timestamp
          changeStep("success");
        }
      } else {
        // Only log server errors if the scan was successful
        // If scan failed, preserve the original calibration error message
        if (!errorRef.current && scanCompleteRef.current) {
          errorLogger("server", {
            message: "Unexpected status code from server: " + data.status,
          });
        } else {
          // Scan failed; navigate to fail with the pre-stored error message
          setLoading(false);
          changeStep("fail");
        }
      }
    } else {
      // Only log server errors if the scan was successful
      if (!errorRef.current && scanCompleteRef.current) {
        errorLogger("server", { message: "No data returned from server." });
      } else {
        // Scan failed; navigate to fail with the pre-stored error message
        setLoading(false);
        changeStep("fail");
      }
    }
  }

  if (step !== pageID) {
    return null;
  }

  // Gets the calibration response from the connector app, then saves the
  // date and time (if it was successful) to the server
  const calibrateDevice = () => {
    if (device.deviceId) {
      setLoading(true); // activate loading spinner
      setScanProgress({});
      setScanId(null);
      setScanSteps(null);
      setReadyToSave(false); // Reset for new scan
      awaitingScanRef.current = true;
      awaitingSaveRef.current = false;
      saveCompletedRef.current = false; // Reset for new scan
      userCancelledRef.current = false; // Reset cancellation flag
      scanCompleteRef.current = false; // Reset for new scan
      errorRef.current = false; // Reset for new scan
      setInlineError("");
      connectorCalibration.refetch();
    } else {
      console.error("WizardCalibrateScan.js - No device id to calibrate.");
      setInlineError("No device selected. Please return and choose a device.");
    }
  };

  const handleCancel = async () => {
    try {
      userCancelledRef.current = true; // Mark as user-initiated cancellation immediately
      setCancelling(true);
      // Disable EventSource first to stop SSE callbacks
      setScanId(null);
      // Clear refs to stop callback processing
      awaitingScanRef.current = false;
      awaitingSaveRef.current = false;
      // Now call API to cancel the scan on the device
      await cancelScan(device.deviceId, scanId);
      // Handle success - clear remaining state
      setScanProgress({});
      setScanSteps(null);
      setLoading(false);
      setReadyToSave(false); // Reset save trigger
      setShowCancelModal(false);
      setCancelling(false);
      saveCompletedRef.current = false; // Reset flag on cancel
    } catch (err) {
      setInlineError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to cancel scan. Please try again.",
      );
      // Cancellation failed; keep scan state intact and clear cancellation flag
      userCancelledRef.current = false;
      setCancelling(false);
      // Keep current state so the UI reflects that cancellation did not complete
    }
  };

  return (
    <CardA
      title='Calibration Scan'
      subtitle={`Keep the calibration chip in the reader for "${device.name}".`}
    >
      <div className='row'>
        {inlineError && (
          <div className='col-12 mb-3'>
            <div
              className='alert alert-danger alert-dismissible fade show d-flex align-items-center'
              role='alert'
            >
              <i className='bi bi-exclamation-triangle-fill me-2'></i>
              <strong>Calibration Error:</strong>&nbsp;{inlineError}
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                onClick={() => setInlineError("")}
              ></button>
            </div>
          </div>
        )}
        {loading && (
          <div className='d-flex align-items-center justify-content-center col-12'>
            <ScanningProgress
              stepNumber={scanProgress?.step_num}
              scanSteps={scanSteps}
              onCancel={
                isLegacyConnector ? null : () => setShowCancelModal(true)
              }
            />
          </div>
        )}
        {!loading && (
          <div className='mt-4 w-100 d-flex justify-content-center gap-3'>
            <button
              type='button'
              className='col-4 btn btn-secondary mx-auto w-25'
              onClick={() => changeStep("return")}
              disabled={loading}
            >
              Back
            </button>
            <button
              type='button'
              className='btn btn-success col-4 mx-auto w-25'
              onClick={calibrateDevice}
              disabled={loading}
            >
              Start Calibration Scan
            </button>
          </div>
        )}
      </div>
      <CancelScanModal
        show={showCancelModal}
        handleClose={() => setShowCancelModal(false)}
        handleConfirm={() => {
          handleCancel();
        }}
        loading={cancelling}
      />
    </CardA>
  );
};

export default WizardCalibrateScan;
